import { useQuery, useQueryClient } from '@tanstack/react-query';
import { templates as tpl } from '@lawbee/domain';
import { Alert, Badge, Button, Card, CardContent, EmptyState, ErrorState, Field, Input, LoadingState, Textarea, cn } from '@lawbee/ui';
import { FileText } from 'lucide-react';
import { useRef, useState } from 'react';
import { api } from '../../../lib/api';
import { useApiMutation } from '../../../lib/hooks';
import { can, useMe } from '../../../lib/me';
import type { UpdateTemplate } from '../../../lib/types';

type TemplatesResponse = { templates: UpdateTemplate[]; variables: string[] };

/** Client update templates (used by stage changes). Exported for the settings page; takes no props. */
export function UpdateTemplatesEditor() {
  const q = useQuery({ queryKey: ['update-templates'], queryFn: () => api.get<TemplatesResponse>('/api/updates/templates') });
  const [selected, setSelected] = useState<string | null>(null);

  if (q.isLoading) return <LoadingState label="Loading templates" />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { templates } = q.data;
  const variables = q.data.variables.length > 0 ? q.data.variables : [...tpl.TEMPLATE_VARIABLES];
  if (templates.length === 0) {
    return <EmptyState icon={<FileText />} title="No templates yet" description="Templates are created with your matter types. Add practice areas in onboarding or settings." />;
  }
  const current = templates.find((t) => t.key === selected) ?? templates[0]!;

  return (
    <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
      <nav aria-label="Templates">
        <ul className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
          {templates.map((t) => (
            <li key={t.key} className="shrink-0">
              <button
                type="button"
                aria-current={t.key === current.key ? 'true' : undefined}
                onClick={() => setSelected(t.key)}
                className={cn(
                  'w-full rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-foreground/[0.05]',
                  t.key === current.key && 'bg-card font-semibold shadow-card hover:bg-card',
                )}
              >
                <span className="block truncate">{t.name}</span>
                <span className="block truncate font-mono text-[11px] font-normal text-muted-foreground">{t.key}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <TemplateForm key={current.key} template={current} variables={variables} />
    </div>
  );
}

type Slot = 'subject' | 'body' | 'smsBody';

function TemplateForm({ template, variables }: { template: UpdateTemplate; variables: string[] }) {
  const me = useMe();
  const qc = useQueryClient();
  const editable = can(me, 'approve_updates');
  const [v, setV] = useState({ name: template.name, subject: template.subject, body: template.body, smsBody: template.smsBody ?? '' });
  const [slot, setSlot] = useState<Slot>('body');
  const refs = {
    subject: useRef<HTMLInputElement>(null),
    body: useRef<HTMLTextAreaElement>(null),
    smsBody: useRef<HTMLTextAreaElement>(null),
  };
  const save = useApiMutation(
    () => api.put(`/api/updates/templates/${encodeURIComponent(template.key)}`, { name: v.name.trim(), subject: v.subject.trim(), body: v.body.trim(), smsBody: v.smsBody.trim() || null }),
    {
      success: 'Template saved',
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['update-templates'] }),
    },
  );

  const insert = (name: string) => {
    const token = `{{${name}}}`;
    const el = refs[slot].current;
    const value = v[slot];
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    setV((prev) => ({ ...prev, [slot]: next }));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const known = new Set(variables);
  const unknown = [...new Set([...tpl.templateVariables(v.subject), ...tpl.templateVariables(v.body), ...tpl.templateVariables(v.smsBody)])].filter((x) => !known.has(x));
  const dirty = v.name !== template.name || v.subject !== template.subject || v.body !== template.body || v.smsBody !== (template.smsBody ?? '');
  const valid = v.name.trim() && v.subject.trim() && v.body.trim() && v.smsBody.length <= 600;

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {!editable ? <Alert variant="info">Only lawyers and owners can change templates.</Alert> : null}
        <Field label="Name" htmlFor="tpl-name" required>
          <Input id="tpl-name" value={v.name} disabled={!editable} onChange={(e) => setV({ ...v, name: e.target.value })} />
        </Field>
        <Field label="Email subject" htmlFor="tpl-subject" required>
          <Input id="tpl-subject" ref={refs.subject} value={v.subject} disabled={!editable} onFocus={() => setSlot('subject')} onChange={(e) => setV({ ...v, subject: e.target.value })} />
        </Field>
        <Field label="Email body" htmlFor="tpl-body" required>
          <Textarea id="tpl-body" ref={refs.body} rows={9} value={v.body} disabled={!editable} onFocus={() => setSlot('body')} onChange={(e) => setV({ ...v, body: e.target.value })} />
        </Field>
        <Field label="SMS text" htmlFor="tpl-sms" description={<span className="figures">{v.smsBody.length} / 600 characters. Leave blank to send a short version of the email.</span>} error={v.smsBody.length > 600 ? 'Keep SMS text under 600 characters.' : undefined}>
          <Textarea id="tpl-sms" ref={refs.smsBody} rows={3} value={v.smsBody} disabled={!editable} onFocus={() => setSlot('smsBody')} onChange={(e) => setV({ ...v, smsBody: e.target.value })} />
        </Field>

        <div className="space-y-2">
          <p className="text-[13px] font-medium">
            Insert a variable <span className="font-normal text-muted-foreground">(into the {slot === 'smsBody' ? 'SMS text' : slot === 'subject' ? 'subject' : 'email body'})</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {variables.map((name) => (
              <button
                key={name}
                type="button"
                disabled={!editable}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insert(name)}
                className="rounded-md bg-muted px-2 py-1 font-mono text-[12px] transition-colors hover:bg-foreground/[0.1] disabled:opacity-50"
              >
                {`{{${name}}}`}
              </button>
            ))}
          </div>
        </div>

        {unknown.length > 0 ? (
          <Alert variant="warning" title="Unknown variables">
            These render as blank: {unknown.map((u) => `{{${u}}}`).join(', ')}
          </Alert>
        ) : null}

        {editable ? (
          <div className="flex items-center justify-end gap-3">
            {dirty ? <Badge variant="warning">Unsaved changes</Badge> : null}
            <Button
              variant="outline"
              disabled={!dirty}
              onClick={() => setV({ name: template.name, subject: template.subject, body: template.body, smsBody: template.smsBody ?? '' })}
            >
              Reset
            </Button>
            <Button onClick={() => save.mutate(undefined)} loading={save.isPending} disabled={!dirty || !valid}>
              Save template
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
